import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GroceryListDetail } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { loadLastStoreId, saveLastStoreId } from '../../lib/lastStore';
import { addDays, todayIso } from '../../lib/planDates';
import { groceryKeys } from '../../pages/groceries/queries';
import { storeQueryKeys } from '../../pages/stores/queryKeys';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { StorePicker } from './StorePicker';

/**
 * Generates a new grocery list from plans in a date range (default: the next
 * 7 days), or a blank list when the range is cleared — PLAN §4.3. The store
 * defaults to the last one used in this browser (§4.7).
 */
export function NewGroceryListDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (list: GroceryListDetail) => void;
}) {
  const qc = useQueryClient();
  const today = todayIso();
  const [useRange, setUseRange] = useState(true);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(addDays(today, 6));
  const [storeId, setStoreId] = useState<string | null>(() => loadLastStoreId());
  const [subtractPantry, setSubtractPantry] = useState(true);
  const [includeRestock, setIncludeRestock] = useState(true);

  const create = useMutation({
    mutationFn: () =>
      api.groceries.create({
        ...(useRange ? { dateFrom, dateTo } : {}),
        storeId,
        subtractPantry,
        includeRestock,
      }),
    onSuccess: async (detail) => {
      saveLastStoreId(detail.list.storeId);
      await qc.invalidateQueries({ queryKey: groceryKeys.all });
      onCreated(detail);
      onClose();
    },
  });

  // Re-read the remembered store each time the dialog opens (another list may have set it).
  useEffect(() => {
    if (open) setStoreId(loadLastStoreId());
  }, [open]);

  // Name the selected store, since it may not be among the picker's first results. A
  // remembered store that no longer exists falls back to "No store".
  const selected = useQuery({
    queryKey: storeQueryKeys.detail(storeId ?? ''),
    queryFn: () => api.stores.get(storeId!),
    enabled: open && storeId !== null,
    retry: false,
  });
  useEffect(() => {
    if (selected.isError) setStoreId(null);
  }, [selected.isError]);

  return (
    <Dialog open={open} onClose={onClose} title="New grocery list">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm font-medium text-ink">
            <input
              type="checkbox"
              checked={useRange}
              onChange={(e) => setUseRange(e.target.checked)}
              className="size-5 accent-brand"
            />
            Build from planned meals
          </label>
          {useRange ? (
            <div className="grid grid-cols-2 gap-3 pl-7">
              <Field
                id="new-list-from"
                label="From"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <Field
                id="new-list-to"
                label="To"
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          ) : (
            <p className="pl-7 text-xs text-muted">Starts blank — add items yourself.</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <StorePicker onPick={(s) => setStoreId(s?.id ?? null)} selectedId={storeId} />
          {storeId !== null && selected.data ? (
            <p className="text-xs text-muted">
              Selected: {selected.data.chain} — {selected.data.name}
            </p>
          ) : null}
        </div>

        {useRange ? (
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={subtractPantry}
                onChange={(e) => setSubtractPantry(e.target.checked)}
                className="size-5 accent-brand"
              />
              Subtract what's already in the pantry
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={includeRestock}
                onChange={(e) => setIncludeRestock(e.target.checked)}
                className="size-5 accent-brand"
              />
              Add pantry staples that are running low
            </label>
          </div>
        ) : null}

        {create.isError ? <Alert tone="error">{messageForError(create.error)}</Alert> : null}

        <div className="flex gap-2">
          <Button
            onClick={() => create.mutate()}
            disabled={create.isPending || (useRange && dateTo < dateFrom)}
          >
            {create.isPending ? 'Creating…' : 'Create list'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
